import React, { useEffect, useState } from 'react';

import Button from '../ui/Button';
import { getFileKind, getUploadStamp, formatBytes } from './fileHelpers';
import {
    XIcon,
    DownloadIcon,
    ChevronRightIcon,
    DocumentIcon,
} from '../ui/icons';

// Full-size lightbox so admins can view an image or PDF before deciding to
// download it. Navigates across the applicant's whole file list with the
// arrow controls / keyboard.
const FilePreviewModal = ({ files, index, onIndex, onClose, loadPreview, downloadFile }) => {
    const open = index != null && index >= 0;
    const file = open ? files[index] : null;
    const kind = file ? getFileKind(file.file_name) : null;

    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight' && index < files.length - 1) onIndex(index + 1);
            if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, index, files, onIndex, onClose]);

    useEffect(() => {
        let active = true;
        setUrl(null);
        setFailed(false);
        if (open && (kind === 'image' || kind === 'pdf')) {
            setLoading(true);
            loadPreview(file)
                .then((res) => {
                    if (!active) return;
                    if (res) setUrl(res.url);
                    else setFailed(true);
                })
                .catch(() => active && setFailed(true))
                .finally(() => active && setLoading(false));
        }
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index]);

    if (!open) return null;

    const canPrev = index > 0;
    const canNext = index < files.length - 1;

    const renderBody = () => {
        if (loading) {
            return <div className="h-14 w-14 animate-spin rounded-full border-4 border-white/30 border-t-white" />;
        }
        if (kind === 'image' && url && !failed) {
            return (
                <img
                    src={url}
                    alt={file.file_name}
                    className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                />
            );
        }
        if (kind === 'pdf' && url && !failed) {
            return <iframe title={file.file_name} src={url} className="h-full w-full rounded-lg bg-white" />;
        }
        return (
            <div className="flex flex-col items-center gap-3 text-white/80">
                <DocumentIcon className="h-16 w-16" />
                <p className="text-sm">
                    {failed ? 'Could not load a preview.' : 'No preview available for this file type.'}
                </p>
                <Button variant="secondary" onClick={() => downloadFile(file)}>
                    <DownloadIcon className="h-4 w-4" />
                    Download to view
                </Button>
            </div>
        );
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex flex-col"
            style={{ backgroundColor: 'rgba(9, 10, 15, 0.9)', backdropFilter: 'blur(2px)' }}
            role="dialog"
            aria-modal="true"
        >
            <div className="flex items-center justify-between gap-4 px-4 py-3 text-white">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{file.original_name || file.file_name}</p>
                    <p className="mt-0.5 truncate text-xs text-white/60">
                        {[
                            `${index + 1} of ${files.length}`,
                            [getUploadStamp(file).date, getUploadStamp(file).time]
                                .filter(Boolean)
                                .join(' '),
                            file.size ? formatBytes(file.size) : '',
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                    <Button variant="secondary" onClick={() => downloadFile(file)}>
                        <DownloadIcon className="h-4 w-4" />
                        Download
                    </Button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close preview"
                        className="rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-4 sm:px-16">
                {canPrev ? (
                    <button
                        type="button"
                        onClick={() => onIndex(index - 1)}
                        aria-label="Previous file"
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors sm:left-4"
                    >
                        <ChevronRightIcon className="h-5 w-5 rotate-180" />
                    </button>
                ) : null}

                <div
                    className="flex h-full w-full items-center justify-center"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) onClose();
                    }}
                >
                    {renderBody()}
                </div>

                {canNext ? (
                    <button
                        type="button"
                        onClick={() => onIndex(index + 1)}
                        aria-label="Next file"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors sm:right-4"
                    >
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default FilePreviewModal;
